/**
 * Shivurt's site header: the real logo art in place of the template's monogram.
 *
 * The supplied PNG is one flat lockup — node mark, "Shivurt" wordmark and a
 * "Discover, Verify , Trade" line — inside a wide transparent margin. Two things
 * follow from that, both measured from the pixels rather than guessed:
 *
 * 1. The margin is cropped away in CSS. Rendered whole, the artwork would shrink
 *    to a fraction of the header's height.
 * 2. The mark and the wordmark are cropped separately and the tagline is left out.
 *    The tagline sits beside the mark's lower half, so no single rectangle holds
 *    the lockup without it — and at header size its type renders about three
 *    pixels tall, i.e. as grey mush. A compact header carries mark + wordmark;
 *    the tagline already appears as real text on the arrival screen.
 *
 * Nav mechanics, the /nav overlay and the mobile sidebar behave exactly as the
 * template's header, which this replaces wholesale (a custom Header owns its own
 * bar, including the small-screen treatment).
 */
import { useEffect, useState, type FC } from 'react';
import { createPortal } from 'react-dom';
import { Menu, Moon, Sun, X } from 'lucide-react';
import { defineMessages, useIntl } from 'react-intl';
import { cn } from '@/app/lib/utils';
import { messages } from '@/app/lib/localization/messages.ts';
import type { AgentHeaderProps } from '@/app/lib/stage/StageHeader.tsx';
import type { NavItem } from '@/app/lib/stage/nav-model.ts';
import { useAppearance } from './theme-mode.ts';

const headerMessages = defineMessages({
  toDark: {
    id: 'header.appearance.toDark',
    defaultMessage: 'Switch to dark mode',
    description:
      'Accessible name of the header sun/moon button while the site is in light mode; pressing it turns the dark appearance on.',
  },
  toLight: {
    id: 'header.appearance.toLight',
    defaultMessage: 'Switch to light mode',
    description:
      'Accessible name of the header sun/moon button while the site is in dark mode; pressing it turns the light appearance on.',
  },
});

const LOGO_URL =
  'https://storage.agentplace.io/U6HbkGgcRoM8Z3lpbOErwz8zS3uyzV5NPltxXyS1-9c/oxp7mlroebia/media/1789171252312-0a651e18-dd22-490a-afc4-68c5efafa2bd-shivurt-logo.png';

const IMAGE = { w: 546, h: 479 };

/** Measured ink bounds inside the 546×479 artwork, in pixels. */
const RECTS = {
  /** The node mark: 106×132, aspect 0.80. */
  mark: { x: 81, y: 176, w: 106, h: 132 },
  /** The "Shivurt" wordmark alone: 189×42, aspect 4.50. */
  word: { x: 241, y: 204, w: 189, h: 42 },
};

/**
 * Blow the image up until the requested rectangle exactly fills the box, then pull
 * the rest outside it. Percentages resolve against the box, so one set of numbers
 * holds at every size the header uses.
 */
const cropStyle = (rect: { x: number; y: number; w: number; h: number }) => ({
  width: `${(IMAGE.w / rect.w) * 100}%`,
  height: `${(IMAGE.h / rect.h) * 100}%`,
  left: `${-(rect.x / rect.w) * 100}%`,
  top: `${-(rect.y / rect.h) * 100}%`,
});

/** `alt=""`: the pair is one image to a screen reader, named once on the wrapper. */
const Crop: FC<{ rect: (typeof RECTS)['mark']; className: string }> = ({ rect, className }) => (
  <div className={cn('relative flex-none overflow-hidden', className)}>
    <img src={LOGO_URL} alt="" className="absolute max-w-none" style={cropStyle(rect)} />
  </div>
);

const BrandLogo: FC<{ label: string; compact?: boolean }> = ({ label, compact = false }) => (
  <div
    // The artwork is drawn on white, nodes included, so the dark appearance sets
    // it on a plate of that same white instead of recolouring the lockup.
    className="flex flex-none items-center gap-2 dark:rounded-md dark:bg-brand-logo-plate dark:px-2 dark:py-1.5 sm:gap-2.5"
    role="img"
    aria-label={label}
    translate="no"
  >
    {/* Widths are height × the measured aspect, so neither piece is stretched. */}
    <Crop
      rect={RECTS.mark}
      className={compact ? 'h-[26px] w-[21px]' : 'h-[26px] w-[21px] sm:h-[32px] sm:w-[26px]'}
    />
    <Crop
      rect={RECTS.word}
      className={compact ? 'h-[18px] w-[81px]' : 'h-[18px] w-[81px] sm:h-[22px] sm:w-[99px]'}
    />
  </div>
);

/**
 * The visitor's light/dark switch, sitting with the brand rather than in the
 * menu: one control, the icon showing the appearance it moves TO, and an
 * accessible name that says what pressing it does.
 */
const AppearanceToggle: FC = () => {
  const intl = useIntl();
  const { mode, toggle } = useAppearance();
  const dark = mode === 'dark';
  const Icon = dark ? Sun : Moon;
  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={intl.formatMessage(dark ? headerMessages.toLight : headerMessages.toDark)}
      aria-pressed={dark}
      className="grid h-9 w-9 flex-none cursor-pointer touch-manipulation place-items-center rounded-md border-none bg-transparent text-muted-foreground transition-colors duration-150 ease-out hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
    >
      <Icon aria-hidden="true" className="h-[18px] w-[18px]" />
    </button>
  );
};

export const ShivurtHeader: FC<AgentHeaderProps> = ({ brandName, navItems, onNavigate }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const intl = useIntl();

  useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [menuOpen]);

  const handleItemClick = (item: NavItem) => {
    setMenuOpen(false);
    onNavigate(item);
  };

  return (
    <header className="flex-none border-b border-border/70 bg-background/80 px-5 pt-[env(safe-area-inset-top)] backdrop-blur-sm md:px-20">
      <div className="mx-auto flex w-full max-w-container-content items-center gap-x-4 py-3 sm:gap-x-6 sm:py-4">
        {/* Box width = height × the artwork's measured aspect, so the lockup keeps
            its proportions at both sizes. */}
        <BrandLogo label={brandName} />

        {/* Sits with the brand, ahead of navigation: it changes how the site
            looks, not where the visitor is. */}
        <AppearanceToggle />

        <nav
          className="scrollbar-none hidden min-w-0 items-center gap-1 overflow-x-auto sm:-mr-3 sm:ml-auto sm:flex"
          aria-label={intl.formatMessage(messages.siteNavigation)}
        >
          {navItems.map((item) => (
            <button
              key={item.label}
              type="button"
              className="flex-none cursor-pointer touch-manipulation whitespace-nowrap rounded-sm border-none bg-transparent px-3 py-2 text-xs font-semibold uppercase tracking-caps text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary data-[active=true]:bg-muted data-[active=true]:text-primary"
              data-active={item.active === true}
              onClick={() => onNavigate(item)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {navItems.length > 0 && (
          <button
            type="button"
            className="ml-auto grid h-9 w-9 cursor-pointer touch-manipulation place-items-center rounded-md border-none bg-transparent text-foreground transition-colors hover:bg-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary sm:hidden"
            aria-label={intl.formatMessage(messages.openMenu)}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <Menu className="h-5 w-5" />
          </button>
        )}
      </div>

      {createPortal(
        <>
          <div
            aria-hidden="true"
            className={cn(
              'fixed inset-0 z-[11] bg-foreground/25 transition-opacity duration-[220ms] sm:hidden',
              menuOpen ? 'opacity-100' : 'pointer-events-none opacity-0',
            )}
            onPointerDown={() => setMenuOpen(false)}
          />
          <div
            className={cn(
              'fixed inset-y-0 right-0 z-[12] flex w-72 flex-col border-l border-border bg-background shadow-lift transition-transform duration-[220ms] ease-in-out sm:hidden',
              menuOpen ? 'translate-x-0' : 'translate-x-full',
            )}
          >
            <div className="flex items-center justify-between py-3 pl-5 pr-3">
              <BrandLogo label={brandName} compact />
              <button
                type="button"
                className="grid h-9 w-9 cursor-pointer touch-manipulation place-items-center rounded-md border-none bg-transparent text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
                aria-label={intl.formatMessage(messages.closeMenu)}
                onClick={() => setMenuOpen(false)}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <nav
              className="flex flex-col gap-1 px-3 pb-4"
              aria-label={intl.formatMessage(messages.siteNavigation)}
            >
              {navItems.map((item) => (
                <button
                  key={item.label}
                  type="button"
                  className="cursor-pointer touch-manipulation rounded-sm border-none bg-transparent px-3 py-3 text-left text-xs font-semibold uppercase tracking-caps text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary data-[active=true]:bg-muted data-[active=true]:text-primary"
                  data-active={item.active === true}
                  tabIndex={menuOpen ? 0 : -1}
                  onClick={() => handleItemClick(item)}
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
        </>,
        document.body,
      )}
    </header>
  );
};
